import { ApiError } from "./client";
import { api } from "./client";
import { useAuthStore } from "@/stores/auth";
import type { Conversation, Message, Uuid } from "@/types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8001";

export interface ConversationListResponse {
  items: Conversation[];
  has_more: boolean;
}

export interface MessageListResponse {
  items: Message[];
  has_more: boolean;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

export interface SearchResultItem {
  id: Uuid;
  title: string | null;
  last_message_at: string;
  matched_field: "title" | "message";
  snippet: string | null;
}

export interface SearchResultResponse {
  items: SearchResultItem[];
  total: number;
}

export const conversationsApi = {
  list: (limit = 50) =>
    api.get<ConversationListResponse>("/api/v1/conversations", {
      query: { limit },
    }),
  create: (title?: string) =>
    api.post<Conversation>("/api/v1/conversations", { title }),
  rename: (convId: Uuid, title: string) =>
    api.patch<Conversation>(`/api/v1/conversations/${convId}`, { title }),
  messages: (convId: Uuid, limit = 100) =>
    api.get<MessageListResponse>(`/api/v1/conversations/${convId}/messages`, {
      query: { limit },
    }),
  archive: (convId: Uuid) =>
    api.delete<void>(`/api/v1/conversations/${convId}`),
  /** Doc 59: server-side search by title + message content. */
  search: (q: string, limit = 30) =>
    api.get<SearchResultResponse>("/api/v1/conversations/search", {
      query: { q, limit },
    }),
  /** Doc 58: export conversation thành markdown blob + filename. */
  export: async (
    convId: Uuid,
    format: "markdown" | "txt" = "markdown",
  ): Promise<ExportResult> => {
    const token = useAuthStore.getState().accessToken;
    const url = new URL(
      `/api/v1/conversations/${convId}/export`,
      BASE_URL,
    );
    url.searchParams.set("format", format);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (!response.ok) {
      let detail = `${response.status} ${response.statusText}`;
      try {
        const body = (await response.json()) as { detail?: string };
        detail = body.detail ?? detail;
      } catch {
        // ignore
      }
      throw new ApiError(response.status, detail);
    }
    // Parse filename from Content-Disposition
    const cd = response.headers.get("content-disposition") ?? "";
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] ?? `conversation_${convId}.md`;
    const blob = await response.blob();
    return { blob, filename };
  },
};
